import { apiClient } from "../client";

export interface BaseMutationInput {
  organizationId: string;
}

interface MutationConfig<TInput extends BaseMutationInput> {
  method: "GET" | "POST" | "DELETE" | "PATCH" | "PUT";
  buildEndpoint: (input: TInput) => string;
  buildBody?: (input: TInput) => any;
}

export function createMutation<TInput extends BaseMutationInput, TResponse>(
  config: MutationConfig<TInput>
): (input: TInput) => Promise<TResponse> {
  return async (input: TInput): Promise<TResponse> => {
    const endpoint = config.buildEndpoint(input);
    const body = config.buildBody?.(input);
    const requestConfig = { organizationId: input.organizationId };

    switch (config.method) {
      case "GET":
        return apiClient.get<TResponse>(endpoint, requestConfig);
      case "POST":
        return apiClient.post<TResponse>(endpoint, body, requestConfig);
      case "DELETE":
        return apiClient.delete<TResponse>(endpoint, body, requestConfig);
      case "PATCH":
        return apiClient.patch<TResponse>(endpoint, body, requestConfig);
      case "PUT":
        return apiClient.put<TResponse>(endpoint, body, requestConfig);
      default:
        throw new Error(`Unsupported method: ${config.method}`);
    }
  };
}

export function createPostMutation<TInput extends BaseMutationInput, TResponse>(
  endpoint: string | ((input: TInput) => string),
  buildBody?: (input: TInput) => any
): (input: TInput) => Promise<TResponse> {
  return createMutation<TInput, TResponse>({
    method: "POST",
    buildEndpoint: typeof endpoint === "string" ? () => endpoint : endpoint,
    buildBody,
  });
}

export function createGetMutation<TInput extends BaseMutationInput, TResponse>(
  endpoint: string | ((input: TInput) => string)
): (input: TInput) => Promise<TResponse> {
  return createMutation<TInput, TResponse>({
    method: "GET",
    buildEndpoint: typeof endpoint === "string" ? () => endpoint : endpoint,
  });
}

export function createDeleteMutation<TInput extends BaseMutationInput, TResponse>(
  endpoint: string | ((input: TInput) => string),
  buildBody?: (input: TInput) => any
): (input: TInput) => Promise<TResponse> {
  return createMutation<TInput, TResponse>({
    method: "DELETE",
    buildEndpoint: typeof endpoint === "string" ? () => endpoint : endpoint,
    buildBody,
  });
}

export function createPatchMutation<TInput extends BaseMutationInput, TResponse>(
  endpoint: string | ((input: TInput) => string),
  buildBody?: (input: TInput) => any
): (input: TInput) => Promise<TResponse> {
  return createMutation<TInput, TResponse>({
    method: "PATCH",
    buildEndpoint: typeof endpoint === "string" ? () => endpoint : endpoint,
    buildBody,
  });
}

export function createPutMutation<TInput extends BaseMutationInput, TResponse>(
  endpoint: string | ((input: TInput) => string),
  buildBody?: (input: TInput) => any
): (input: TInput) => Promise<TResponse> {
  return createMutation<TInput, TResponse>({
    method: "PUT",
    buildEndpoint: typeof endpoint === "string" ? () => endpoint : endpoint,
    buildBody,
  });
}
